import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '../../../../../shared/source/context/context';
import { jobProfilesOptionsResponseSchema, jobProfilesResponseSchema } from '../../../job-profile/job-profile-schema';

import type { FilterRequest } from '../../../../../shared/source/data-contract/filter';
import type { Pathname } from '../../../../../shared/source/http-fetcher/http-fetcher';
import type {
    JobProfilesOptionsResponse,
    JobProfilesPermissions,
    JobProfilesResponse,
} from '../../../job-profile/job-profile-schema';
import type { UseQueryResult } from '@tanstack/react-query';
import type Result from 'true-myth/result';

const optionsPathname: Pathname = '/job-profiles/options';

const filterPathname: Pathname = `/job-profiles/filter`;

export const useFilterJobProfiles = (
    { filter, paged = { page: 0, size: 20 }, sorted }: FilterRequest,
    enabled = true,
): UseQueryResult<JobProfilesResponse, Error> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    return useQuery({
        enabled,
        placeholderData: keepPreviousData,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.postJson(jobProfilesResponseSchema, {
                pathname: filterPathname,
                payload: {
                    filter,
                    paged,
                    sorted,
                },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [filterPathname, 'postJson', jobProfilesResponseSchema, filter, paged, sorted],
    });
};

export type JobProfilesOptionsResult = UseQueryResult<Result<JobProfilesOptionsResponse, Error>>;

export const useJobProfilesOptionsQuery = (): JobProfilesOptionsResult => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    return useQuery({
        queryFn: async () =>
            validatingCobrainerApiFetcher.get(jobProfilesOptionsResponseSchema, {
                pathname: optionsPathname,
            }),
        queryKey: [optionsPathname, 'get', jobProfilesOptionsResponseSchema],
        staleTime: 10_000,
    });
};

export const useJobProfilesPermissions = (): JobProfilesPermissions & {
    readonly isPending: boolean;
} => {
    const { data, isPending } = useJobProfilesOptionsQuery();

    if (data?.isOk === true) {
        return {
            canUserGetJobProfiles: data.value.readMany.enabled,
            isPending,
        };
    }

    return {
        canUserGetJobProfiles: false,
        isPending,
    };
};
